<template>
  <div class="evidence-telemetry-chart bg-white rounded-xl pa-5">
    <!-- 图表顶栏：极简标题与测点切换 Tab -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-3 pb-3 border-b-subtle">
      <div class="d-flex align-center">
        <v-icon size="18" color="#2563eb" class="mr-1-5">mdi-chart-timeline-variant</v-icon>
        <span class="font-weight-bold text-slate-800 text-subtitle-2 mr-2">时序取证趋势</span>
        <span class="text-caption text-slate-400">
          (判定窗口: {{ windowDisplayText }})
        </span>
      </div>

      <!-- 测点快速切换 (独立量纲聚焦，无黑框扁平切换) -->
      <div class="sensor-tabs d-flex align-center ga-1 bg-slate-100 p-1 rounded-lg">
        <button
          v-for="s in primarySeriesList"
          :key="s.seriesId"
          type="button"
          class="sensor-tab-btn"
          :class="{ active: currentSeriesId === s.seriesId }"
          @click="currentSeriesId = s.seriesId"
        >
          {{ s.label }}
          <span v-if="s.threshold != null" class="ml-1 font-weight-normal opacity-75">
            ({{ s.threshold }}{{ s.unit }})
          </span>
        </button>
        <button
          v-if="auxiliarySeriesList.length"
          type="button"
          class="sensor-tab-btn"
          :class="{ active: currentSeriesId === 'aux' }"
          @click="currentSeriesId = 'aux'"
        >
          旁证环境 ({{ auxiliarySeriesList.length }})
        </button>
      </div>
    </div>

    <!-- 当前选定测点的核心状态指示条 -->
    <div class="current-sensor-info d-flex align-center justify-space-between mb-2 text-caption">
      <div class="d-flex align-center ga-3">
        <span class="text-slate-700">
          当前监控测点：<strong class="text-slate-900">{{ activeSensorName }}</strong>
        </span>
        <span v-if="activeSensorThreshold != null" class="text-amber-800 font-weight-medium">
          报警阈值线：{{ activeSensorThreshold }}
        </span>
        <span class="d-inline-flex align-center ga-1 text-red-600 font-weight-medium">
          <span class="red-line-dot mr-1" /> 告警发生瞬时 ({{ alarmTimeSimple }})
        </span>
      </div>

      <div class="text-slate-400">
        <span v-if="hasDataInCurrent">采样点数: {{ currentDataCount }}</span>
        <span v-else-if="activeSensorStatusText" class="text-amber-700 font-weight-medium">
          {{ activeSensorStatusText }}
        </span>
        <span v-else class="text-slate-400">本时段无连续时序采样点</span>
      </div>
    </div>

    <!-- 图表核心区域 -->
    <div class="chart-box rounded-lg">
      <div ref="chartDomRef" class="chart-dom" />
    </div>

    <!-- 底栏说明：彻底去除所有小标签黑框，采用纯文本小圆点优雅分隔 -->
    <div class="chart-footer-note d-flex flex-wrap align-center justify-space-between pt-3 mt-2 text-caption border-t-subtle ga-3">
      <!-- 左侧：超级表取证事实（纯净平铺，无任何标签线框） -->
      <div class="d-flex align-center flex-wrap ga-2 text-slate-500">
        <span class="font-weight-medium text-slate-700 d-inline-flex align-center">
          <v-icon size="14" color="#0284c7" class="mr-1-5">mdi-database-check-outline</v-icon>
          超级表取证支撑 ({{ queriesIssued }} 次查询)：
        </span>
        <span v-for="(s, sIdx) in sources" :key="s.stable" class="d-inline-flex align-center ga-1">
          <span v-if="sIdx > 0" class="text-slate-300 mx-1">·</span>
          <span class="text-slate-600">{{ s.label }}</span>
          <strong class="text-slate-800">{{ s.rowCount }} 行</strong>
          <span v-if="s.status !== 'ok'" class="text-amber-700 font-weight-medium">
            ({{ s.status === 'no_device_series' ? '无设备时序' : s.status }})
          </span>
        </span>
      </div>

      <!-- 右侧：图例与耗时指标 -->
      <div class="d-flex align-center ga-3 text-slate-400">
        <span class="d-inline-flex align-center ga-1 text-slate-500">
          <span class="legend-box dashed-thresh" /> 触发阈值线
        </span>
        <span v-if="elapsedMs != null">耗时 {{ elapsedMs }}ms</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as echarts from 'echarts'

const props = defineProps({
  charts: { type: Object, default: null },
  sources: { type: Array, default: () => [] },
  queriesIssued: { type: Number, default: 4 },
  elapsedMs: { type: Number, default: null },
})

const chartDomRef = ref(null)
let chartInstance = null
const currentSeriesId = ref('') // 默认选中首个判定测点

const COLOR_MAP = {
  co: '#2563eb',
  pack_temp: '#d97706',
  cell_temp: '#dc2626',
  aircon_cabinet_in_temp: '#059669',
  cabinet_ambient_temp: '#7c3aed',
  cabinet_spraying: '#0891b2',
}

const allSeries = computed(() => props.charts?.series || [])

// 拆分：主判据测点 vs 旁证测点
const primarySeriesList = computed(() => {
  return allSeries.value.filter(s => s.kind === 'condition')
})

const auxiliarySeriesList = computed(() => {
  return allSeries.value.filter(s => s.kind === 'auxiliary')
})

// 初始化默认选中项
watch(primarySeriesList, (list) => {
  if (list.length && (!currentSeriesId.value || currentSeriesId.value === 'all')) {
    currentSeriesId.value = list[0].seriesId
  }
}, { immediate: true })

const activeSensor = computed(() => {
  if (currentSeriesId.value === 'aux') return null
  return allSeries.value.find(s => s.seriesId === currentSeriesId.value) || primarySeriesList.value[0]
})

const activeSensorName = computed(() => {
  if (currentSeriesId.value === 'aux') return '旁证环境与状态综合 (柜温 / 空调 / 喷洒)'
  if (!activeSensor.value) return '—'
  return `${activeSensor.value.label || activeSensor.value.point} (${activeSensor.value.unit || ''})`
})

const activeSensorThreshold = computed(() => {
  if (!activeSensor.value || activeSensor.value.threshold == null) return null
  return `${activeSensor.value.op || '>'} ${activeSensor.value.threshold} ${activeSensor.value.unit || ''}`
})

const windowDisplayText = computed(() => {
  const w = props.charts?.window
  if (!w || !w.start || !w.end) return '告警前后 30 分钟'
  const s = String(w.start).replace('T', ' ').slice(11, 19)
  const e = String(w.end).replace('T', ' ').slice(11, 19)
  return `${s} ~ ${e}`
})

const alarmTimeSimple = computed(() => {
  const t = props.charts?.alarmTime
  return t ? String(t).replace('T', ' ').slice(11, 19) : ''
})

const hasDataInCurrent = computed(() => {
  if (currentSeriesId.value === 'aux') {
    return auxiliarySeriesList.value.some(s => Array.isArray(s.buckets) && s.buckets.length > 0)
  }
  return Array.isArray(activeSensor.value?.buckets) && activeSensor.value.buckets.length > 0
})

const currentDataCount = computed(() => {
  if (!activeSensor.value) return 0
  return Array.isArray(activeSensor.value.buckets) ? activeSensor.value.buckets.length : 0
})

const activeSensorStatusText = computed(() => {
  if (currentSeriesId.value === 'aux') return null
  const s = activeSensor.value
  if (!s) return null
  if (s.status === 'no_device_series') return '超级表无该设备时序'
  if (s.status === 'no_data') return '窗口内无连续采样'
  if (s.truncated) return '取数触顶截断'
  return null
})

// 解析本地时间为毫秒时间戳（严格规避时区计算偏差）
function parseLocalTimestamp(isoStr) {
  if (!isoStr) return null
  const s = String(isoStr).replace('T', ' ').replace(/\.\d+/, '')
  const parts = s.split(/[- :]/)
  if (parts.length >= 6) {
    return new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2]),
      Number(parts[3]),
      Number(parts[4]),
      Number(parts[5])
    ).getTime()
  }
  return null
}

function formatTimeOnly(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

// 智能断档检测与折线物理断开构造
function processSeriesDataPoints(buckets) {
  if (!buckets || !buckets.length) return []

  const raw = buckets
    .map(b => {
      const ts = parseLocalTimestamp(b.t)
      const val = b.comparedPeak != null ? Number(b.comparedPeak) : (b.val != null ? Number(b.val) : null)
      return [ts, val]
    })
    .filter(p => p[0] != null)
    .sort((a, b) => a[0] - b[0])

  if (raw.length < 2) return raw

  // 统计采样步长中位数
  const stepDiffs = []
  for (let i = 0; i < raw.length - 1; i++) {
    const diff = raw[i + 1][0] - raw[i][0]
    if (diff > 0) stepDiffs.push(diff)
  }
  stepDiffs.sort((a, b) => a - b)
  const baseStep = stepDiffs.length ? stepDiffs[Math.floor(stepDiffs.length / 2)] : 60000
  // 断档阈值：大于基准采样的 1.8 倍，且至少为 3 分钟
  const gapThreshold = Math.max(Math.round(baseStep * 1.8), 3 * 60 * 1000)

  const dataPoints = []

  for (let i = 0; i < raw.length; i++) {
    dataPoints.push(raw[i])

    if (i < raw.length - 1) {
      const t1 = raw[i][0]
      const t2 = raw[i + 1][0]
      const diff = t2 - t1

      if (diff >= gapThreshold) {
        // 在 t1 之后与 t2 之前插入 null 点，在 ECharts 时间轴上彻底断开折线（严禁连线）
        const pad = Math.min(Math.round(baseStep * 0.5), Math.round(diff * 0.1))
        dataPoints.push([t1 + pad, null])
        dataPoints.push([t2 - pad, null])
      }
    }
  }

  return dataPoints
}

function initOrUpdateChart() {
  if (!chartDomRef.value || !props.charts) return

  if (!chartInstance) {
    chartInstance = echarts.init(chartDomRef.value)
  }

  const charts = props.charts
  const windowStartTs = parseLocalTimestamp(charts.window?.start)
  const windowEndTs = parseLocalTimestamp(charts.window?.end)
  const alarmTimeTs = parseLocalTimestamp(charts.alarmTime)

  // 决定当前渲染的 series 列表
  let displaySeries = []
  if (currentSeriesId.value === 'aux') {
    displaySeries = auxiliarySeriesList.value
  } else {
    const s = activeSensor.value
    if (s) displaySeries = [s]
  }

  // 计算 Y 轴上下限
  const yVals = []
  displaySeries.forEach(s => {
    if (s.threshold != null) yVals.push(Number(s.threshold))
    ;(s.buckets || []).forEach(b => {
      if (b.comparedPeak != null) yVals.push(Number(b.comparedPeak))
    })
  })

  let yMin = 0
  let yMax = 100
  if (yVals.length > 0) {
    const maxVal = Math.max(...yVals)
    yMax = Math.ceil(maxVal * 1.18)
    const minVal = Math.min(...yVals)
    yMin = minVal < 0 ? Math.floor(minVal * 1.1) : 0
  }

  const echartsSeries = displaySeries.map((s, idx) => {
    const color = COLOR_MAP[s.seriesId] || '#2563eb'
    const dataPoints = processSeriesDataPoints(s.buckets || [])

    const markLineData = []

    // 在第一条曲线上标记告警发生瞬时参考线
    if (idx === 0 && alarmTimeTs != null) {
      markLineData.push({
        xAxis: alarmTimeTs,
        lineStyle: { color: '#dc2626', width: 2, type: 'solid' },
        label: {
          show: true,
          position: 'insideEndTop',
          formatter: '告警发生瞬时',
          color: '#dc2626',
          fontSize: 11,
          fontWeight: 'bold',
        },
      })
    }

    // 若有阈值，绘制横向阈值参考线
    if (s.threshold != null) {
      markLineData.push({
        yAxis: s.threshold,
        lineStyle: { color: '#d97706', width: 1.5, type: 'dashed' },
        label: {
          show: true,
          position: 'insideEndTop',
          formatter: `触发阈值: ${s.threshold} ${s.unit || ''}`,
          color: '#b45309',
          fontSize: 11,
          fontWeight: '500',
        },
      })
    }

    return {
      name: s.label || s.point,
      type: 'line',
      smooth: false,
      connectNulls: false, // 没数据时物理断开显示，严禁虚假连线
      showSymbol: dataPoints.length < 60,
      symbolSize: 4,
      itemStyle: { color },
      lineStyle: { width: 2, color },
      data: dataPoints,
      markLine: markLineData.length ? { silent: true, symbol: 'none', data: markLineData } : undefined,
    }
  })

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: 'transparent',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      borderWidth: 0,
      padding: [8, 12],
      textStyle: { color: '#1e293b', fontSize: 12 },
      formatter: (params) => {
        if (!params || !params.length) return ''
        const firstTs = params[0].value?.[0]
        const timeStr = formatTimeOnly(firstTs)
        let html = `<div style="font-weight:bold;margin-bottom:4px;color:#0f172a">${timeStr}</div>`
        params.forEach(item => {
          const val = item.value?.[1]
          const valText = val != null ? Number(val.toFixed(1)) : '— (无数据/中断)'
          html += `
            <div style="display:flex;align-items:center;justify-content:space-between;gap:16px;">
              <span style="color:#475569">${item.seriesName}</span>
              <strong style="font-family:inherit;color:${val != null ? '#0f172a' : '#dc2626'};font-weight:600">${valText}</strong>
            </div>
          `
        })
        return html
      },
    },
    grid: {
      top: 36,
      left: 48,
      right: 30,
      bottom: 24,
    },
    xAxis: {
      type: 'time',
      min: windowStartTs != null ? windowStartTs : undefined,
      max: windowEndTs != null ? windowEndTs : undefined,
      axisLine: { lineStyle: { color: '#e2e8f0' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 11,
        formatter: (val) => formatTimeOnly(val),
      },
      splitLine: { show: true, lineStyle: { color: '#f8fafc' } },
    },
    yAxis: {
      type: 'value',
      min: yMin,
      max: yMax,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#64748b', fontSize: 11 },
      splitLine: { lineStyle: { color: '#f8fafc' } },
    },
    series: echartsSeries,
  }

  chartInstance.setOption(option, true)
}

function handleResize() {
  chartInstance?.resize()
}

watch([() => props.charts, currentSeriesId], () => {
  nextTick(() => {
    initOrUpdateChart()
  })
}, { deep: true })

onMounted(() => {
  nextTick(() => {
    initOrUpdateChart()
    window.addEventListener('resize', handleResize)
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  chartInstance?.dispose()
  chartInstance = null
})
</script>

<style scoped lang="scss">
.evidence-telemetry-chart {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.border-b-subtle {
  border-bottom: 1px solid #f8fafc;
}

.border-t-subtle {
  border-top: 1px solid #f8fafc;
}

.sensor-tabs {
  background: #f8fafc;
}

.sensor-tab-btn {
  border: none;
  background: transparent;
  padding: 4px 12px;
  font-size: 12px;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #1e293b;
  }

  &.active {
    background: #ffffff;
    color: #2563eb;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.chart-box {
  height: 280px;
  background: #fafbfc;
}

.chart-dom {
  width: 100%;
  height: 100%;
}

.red-line-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #dc2626;
  border-radius: 50%;
}

.legend-box {
  display: inline-block;
  width: 12px;
  height: 8px;
  vertical-align: middle;
  border-radius: 2px;

  &.dashed-thresh {
    border-top: 1.5px dashed #d97706;
    height: 0;
  }
}

.mr-1-5 {
  margin-right: 6px !important;
}
</style>
